import React from "react";
import { format, parseISO } from "date-fns";

// "WorkOrder" -> "Work Order", "TaxAdjustment" -> "Tax Adjustment",
// "SUPPLIER_INVOICE" -> "Supplier Invoice"
function formatTranType(raw) {
   if (!raw) return "";
   return String(raw)
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtAmount(value) {
   const n = Number(value);
   return Number.isFinite(n) ? n.toFixed(2) : "";
}

function fmtPostedDate(iso) {
   if (!iso) return "";
   try {
      return format(parseISO(iso), "yyyy-MM-dd");
   } catch {
      return "";
   }
}

function ConfidenceBadge({ confidence }) {
   if (confidence === null || confidence === undefined) return null;
   const pct = Math.round(Number(confidence) * 100);
   if (!Number.isFinite(pct)) return null;
   return <span className={`recon-conf${pct < 90 ? " low" : ""}`}>{pct}%</span>;
}

function StatementPane({ rowData, stmt }) {
   return (
      <div className="recon-pane">
         <h4>Supplier Statement</h4>
         <dl className="recon-fields">
            <dt>Invoice #</dt>
            <dd>
               {rowData.sd_parsed_invoice_no || rowData.sd_invoice_no || "—"}
               {rowData.sd_invoice_no && <span className="recon-scan">scanned "{rowData.sd_invoice_no}"</span>}
               <ConfidenceBadge confidence={stmt?.invoice_id?.confidence} />
            </dd>
            <dt>Date</dt>
            <dd>
               {rowData.sd_invoice_date || "—"}
               {stmt?.invoice_date?.value && <span className="recon-scan">scanned "{stmt.invoice_date.value}"</span>}
               <ConfidenceBadge confidence={stmt?.invoice_date?.confidence} />
            </dd>
            <dt>Amount</dt>
            <dd>
               {fmtAmount(rowData.sd_amount) || "—"}
               {stmt?.amount?.value && <span className="recon-scan">scanned "{stmt.amount.value}"</span>}
               <ConfidenceBadge confidence={stmt?.amount?.confidence} />
            </dd>
            <dt>PO No</dt>
            <dd>
               {rowData.sd_po_no || "—"}
               {stmt?.po_no?.value && <span className="recon-scan">scanned "{stmt.po_no.value}"</span>}
               <ConfidenceBadge confidence={stmt?.po_no?.confidence} />
            </dd>
         </dl>
      </div>
   );
}

function ProtractorSkeleton() {
   return (
      <div className="recon-skeleton">
         <div className="recon-sk" style={{ width: "55%" }} />
         <div className="recon-sk" style={{ width: "45%" }} />
         <div className="recon-sk" style={{ width: "60%" }} />
         <div className="recon-sk recon-sk-gap" style={{ width: "90%" }} />
         <div className="recon-sk" style={{ width: "75%" }} />
         <div className="recon-sk" style={{ width: "80%" }} />
      </div>
   );
}

function ProtractorLoaded({ rowData, erp }) {
   const details = Array.isArray(erp?.details) ? erp.details : [];
   const lineTotal = details.reduce((sum, d) => sum + (Number(d.total_amount) || 0), 0);
   const total = details.length ? lineTotal : Number(rowData.en_invoice_amount) || 0;

   return (
      <>
         <dl className="recon-fields">
            <dt>Invoice #</dt>
            <dd>{rowData.esh_parsed_invoice_id || rowData.en_invoice_id || "—"}</dd>
            <dt>Tran type</dt>
            <dd>{formatTranType(erp?.tran_type) || "—"}</dd>
            <dt>Invoice date</dt>
            <dd>{erp?.invoice_date || "—"}</dd>
            <dt>Total tax</dt>
            <dd>{fmtAmount(erp?.total_tax) || "—"}</dd>
         </dl>
         {details.length === 0 ? (
            <div className="recon-li-empty">No line items.</div>
         ) : (
            <table className="recon-li">
               <thead>
                  <tr>
                     <th>Type</th>
                     <th>Description</th>
                     <th className="num">Amount</th>
                  </tr>
               </thead>
               <tbody>
                  {details.map((d) => {
                     const isTax = /tax/i.test(d.tran_type || "");
                     return (
                        <tr key={d.eshd_id}>
                           <td>
                              <span className={`recon-tt${isTax ? " tax" : ""}`}>{formatTranType(d.tran_type)}</span>
                           </td>
                           <td>{d.description}</td>
                           <td className="num">{fmtAmount(d.total_amount)}</td>
                        </tr>
                     );
                  })}
               </tbody>
               <tfoot>
                  <tr>
                     <td colSpan={2} className="recon-li-total-lbl">
                        Total
                     </td>
                     <td className="num">{fmtAmount(total)}</td>
                  </tr>
               </tfoot>
            </table>
         )}
         {(erp?.user_name || erp?.inserted_on) && (
            <div className="recon-erp-meta">
               Posted by {erp?.user_name || "—"}
               {erp?.inserted_on ? ` · ${fmtPostedDate(erp.inserted_on)}` : ""}
            </div>
         )}
      </>
   );
}

function ProtractorPane({ rowData, detail, onRetry }) {
   const status = detail?.status;
   return (
      <div className="recon-pane erp">
         <h4>
            Protractor Entry
            {status === "loading" && <span className="recon-spinner" />}
         </h4>
         {status === "loading" && <ProtractorSkeleton />}
         {status === "empty" && <div className="recon-empty">No Protractor entry is matched to this statement line.</div>}
         {status === "error" && (
            <div className="recon-empty">
               Couldn't load Protractor detail.
               <button type="button" className="recon-retry" onClick={onRetry}>
                  Retry
               </button>
            </div>
         )}
         {status === "loaded" && <ProtractorLoaded rowData={rowData} erp={detail?.data?.erp_detail} />}
      </div>
   );
}

function ReconcileLineDetailPanel({ rowData, detail, onRetry }) {
   return (
      <tr className="recon-detail-row">
         <td className="recon-detail-spacer" />
         <td colSpan={6} className="recon-detail-cell">
            <StatementPane rowData={rowData} stmt={detail?.data?.statement_detail} />
         </td>
         <td className="recon-detail-spacer" />
         <td colSpan={4} className="recon-detail-cell">
            <ProtractorPane rowData={rowData} detail={detail} onRetry={onRetry} />
         </td>
         <td className="recon-detail-spacer" />
      </tr>
   );
}

export default ReconcileLineDetailPanel;
